import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BookMarked, ExternalLink, Trash2, Plus, Lock, FileText, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { MemberQuickNav } from '../MemberQuickNav';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';

export default function LeadershipLibraryPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ title: '', url: '', description: '' });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isAdmin = user?.role === 'admin';

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!form.title.trim()) {
      toast.error('Enter a title first, then choose the file.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', form.title);
    fd.append('description', form.description);
    try {
      await api.post('/admin/leadership-library/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded to the library.');
      setForm({ title: '', url: '', description: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const load = useCallback(() => {
    api.get('/leadership-library').then((res) => setData(res.data)).catch(() => setData({ access: false, items: [] }));
  }, []);
  useEffect(load, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/leadership-library', form);
      toast.success('Resource added.');
      setForm({ title: '', url: '', description: '' });
      load();
    } catch {
      toast.error('Could not add resource.');
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/leadership-library/${id}`);
      load();
    } catch {
      toast.error('Delete failed.');
    }
  };

  return (
    <main className="min-h-screen bg-[#f1e3eb] pt-44 pb-20 px-4" data-testid="leadership-library-page">
      <div className="max-w-3xl mx-auto">
        <MemberQuickNav />
        <div className="text-center mb-10">
          <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">Club Leadership</span>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-2">Leadership Library</h1>
        </div>

        {data === null ? (
          <p className="text-center text-[#3c4a2a]/50">Loading…</p>
        ) : !data.access ? (
          <div className="text-center bg-white rounded-xl shadow-sm p-10" data-testid="leadership-no-access">
            <Lock className="w-10 h-10 text-[#bd8cae] mx-auto mb-3" />
            <p className="text-[#3c4a2a]/70">
              This library is reserved for club leadership. If you believe you should have access, please contact a club administrator.
            </p>
          </div>
        ) : (
          <>
            {isAdmin && (
              <form onSubmit={add} className="bg-white rounded-xl shadow-sm border border-[#bd8cae]/20 p-5 mb-8 space-y-3" data-testid="leadership-add-form">
                <p className="text-sm font-medium text-[#3c4a2a]">Add a resource (admins only)</p>
                <Input required placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="leadership-title-input" />
                <Input placeholder="Link (optional)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="leadership-url-input" />
                <Input placeholder="Short description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="leadership-desc-input" />
                <div className="flex flex-wrap gap-3">
                  <Button type="submit" data-testid="leadership-add-btn" className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white">
                    <Plus className="w-4 h-4 mr-2" /> Add Link
                  </Button>
                  <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,application/pdf" onChange={uploadFile} className="hidden" data-testid="leadership-file-input" />
                  <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="leadership-upload-btn" className="border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white">
                    <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Uploading…' : 'Upload PDF / Doc'}
                  </Button>
                </div>
                <p className="text-xs text-[#3c4a2a]/50">To upload bylaws or agendas: enter a title, then click Upload (PDF or Word, under 15 MB).</p>
              </form>
            )}

            {data.items.length === 0 ? (
              <div className="text-center bg-white rounded-xl shadow-sm p-10" data-testid="leadership-empty">
                <BookMarked className="w-10 h-10 text-[#bd8cae] mx-auto mb-3" />
                <p className="text-[#3c4a2a]/70">
                  The library is being assembled — leadership guides, club bylaws, GFWC resources, and planning documents will live here.
                </p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="leadership-items">
                {data.items.map((r) => (
                  <div key={r.id} className="flex items-start justify-between bg-white rounded-lg shadow-sm border border-[#bd8cae]/20 p-5">
                    <div className="min-w-0">
                      <p className="font-medium text-[#3c4a2a] flex items-center gap-2">
                        {r.file_url ? <FileText className="w-4 h-4 text-[#a3688f] flex-shrink-0" /> : <BookMarked className="w-4 h-4 text-[#a3688f] flex-shrink-0" />}
                        {r.file_url ? (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${r.file_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-[#5d6b3c] underline underline-offset-2 flex items-center gap-1"
                            data-testid={`leadership-file-link-${r.id}`}
                          >
                            {r.title} <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        ) : r.url ? (
                          <a href={r.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#5d6b3c] underline underline-offset-2 flex items-center gap-1">
                            {r.title} <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        ) : r.title}
                      </p>
                      {r.file_name && <p className="text-xs text-[#3c4a2a]/45 mt-0.5">{r.file_name}</p>}
                      {r.description && <p className="text-sm text-[#3c4a2a]/70 mt-1">{r.description}</p>}
                    </div>
                    {isAdmin && (
                      <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`leadership-delete-${r.id}`} className="text-red-500 hover:text-red-700 flex-shrink-0 ml-3">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
